import React, { useState } from "react";
import "./WmsTable.scss";

interface WmsTableColumn {
    field: string;
    title: string;
    width: number;
}

interface WmsTableProps {
    data: any[];
    columns: WmsTableColumn[];
    className?: string;
    onSort?: (field: string, direction: "asc" | "desc") => void;
}

const WmsTable: React.FC<WmsTableProps> = ({ data, columns, className = "", onSort }) => {
    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: "asc" | "desc";
    } | null>(null);

    const handleSort = (key: string) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        // Call external sort handler if provided
        if (onSort) {
            onSort(key, direction);
        }
    };

    const sortedData = React.useMemo(() => {
        if (!sortConfig) return data;

        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === "asc" ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === "asc" ? 1 : -1;
            }
            return 0;
        });
    }, [data, sortConfig]);

    const getSortIcon = (key: string) => {
        if (!sortConfig || sortConfig.key !== key) {
            return "";
        }
        return sortConfig.direction === "asc" ? "↑" : "↓";
    };

    return (
        <div className={`wms-table-container ${className}`}>
            <table className="wms-table">
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th
                                key={column.field}
                                style={{ width: column.width }}
                                onClick={() => handleSort(column.field)}
                                className="wms-table-header"
                            >
                                <div className="header-content">
                                    <span>{column.title}</span>
                                    {getSortIcon(column.field) && (
                                        <span className="sort-icon">{getSortIcon(column.field)}</span>
                                    )}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedData.map((item, index) => (
                        <tr key={index}>
                            {columns.map((column) => (
                                <td key={column.field} className="wms-table-cell">
                                    {item[column.field]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default WmsTable;
